import React, { useEffect, useState } from "react";
import {
  getTenantById,
  getTenantShareholders,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import { ImNewTab } from "react-icons/im";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import saveAs from "file-saver";
import {  Paragraph } from "docx";
import { RiDownloadLine } from "react-icons/ri";
interface Citation {
  citations: string[];
  search_results: SearchResult[];
  content: OwnershipContent;
}

interface SearchResult {
  url: string;
  date: string | null;
  title: string;
  last_updated: string | null;
}

interface OwnershipContent {
  Ownership: string;
  Primary_Owner: string;
  Ownership_Percent: string;
  Ownership_Type: string;
  Other_Shareholders: Shareholder[];
  Data_Availability: string;
  Transactions: Transaction[];
}

interface Shareholder {
  Name: string;
  Company_Name: string;
  Company_Website: string;
  Ownership_Percent: string;
  Type: string;
  Sources: Source[];
}

interface Transaction {
  Date: string;
  Event: string;
  Details: string;
  Sell_Side_Advisors: string[];
  Buy_Side_Advisors: string[];
  Sources: Source[];
}

interface Source {
  Title: string;
  URL: string;
}

interface OwnershipSectionProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

export const OwnershipSection: React.FC<OwnershipSectionProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<Citation | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response = await getTenantShareholders(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
      } catch (error) {
        console.error("Error fetching tenant overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return dateString;

    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const year = date.getFullYear();

      return `${day} ${month}, ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatEventName = (event: string) => {
    const words = event.split(" ");
    return {
      firstRow: words.slice(0, 4).join(" "),
      secondRow: words.slice(4).join(" "),
    };
  };
  // Helper function to extract hostname from URL
  const getHostname = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace("www.", ""); // Remove www. prefix if present
    } catch (error) {
      // If URL parsing fails, try to extract hostname manually
      const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
      return match ? match[1] : url;
    }
  };
  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const handleOpenInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      if (!data) return;

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const leftMargin = 15;
      const rightMargin = 15;
      const contentWidth = pageWidth - leftMargin - rightMargin;

      // Load tenant logo as watermark, if available (optional)
      let watermarkImgData: string | null = null;
      if (tenant?.logoUrl) {
        try {
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();
          watermarkImgData = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch {
          // ignore errors
        }
      }

      const addWatermark = (pageNumber: number) => {
        if (!watermarkImgData) return;
        try {
          doc.setPage(pageNumber);
          doc.saveGraphicsState();
          doc.setGState(doc.GState({ opacity: 0.1 }));

          const watermarkWidth = 60;
          const watermarkHeight = 60;
          const xPos = (pageWidth - watermarkWidth) / 2;
          const yPos = (pageHeight - watermarkHeight) / 2;

          const imgType = watermarkImgData.includes("png") ? "PNG" : "JPEG";
          doc.addImage(
            watermarkImgData,
            imgType,
            xPos,
            yPos,
            watermarkWidth,
            watermarkHeight
          );
          doc.restoreGraphicsState();
        } catch {
          // ignore watermark errors
        }
      };

      /* Header */
      doc.setFontSize(17);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Ownership - Company Overview", pageWidth / 2, 22, {
        align: "center",
      });

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 30, {
        align: "center",
      });

      let y = 45;

      /* Ownership Information */
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Ownership Information", leftMargin, y);
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      const ownershipInfo = [
        `Ownership Type: ${data.content.Ownership}`,
        `Primary Owner: ${data.content.Primary_Owner}`,
        `Ownership Percentage: ${data.content.Ownership_Percent}`,
        `Owner Type: ${data.content.Ownership_Type}`,
      ];

      for (const info of ownershipInfo) {
        const lines = doc.splitTextToSize(info, contentWidth - 4);
        doc.text(lines, leftMargin + 4, y);
        y += lines.length * 5 + 2;
      }

      y += 5;

      /* Other Shareholders Table */
      if (
        data.content.Other_Shareholders &&
        data.content.Other_Shareholders.length > 0
      ) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Other Shareholders", leftMargin, y);
        y += 6;

        const shareholderHeaders = [["Name", "Company", "Ownership %", "Type"]];
        const shareholderRows = data.content.Other_Shareholders.map(
          (shareholder) => [
            shareholder.Name || "",
            shareholder.Company_Name || "",
            shareholder.Ownership_Percent || "",
            shareholder.Type || "",
          ]
        );

        autoTable(doc, {
          startY: y,
          head: shareholderHeaders,
          body: shareholderRows,
          styles: {
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
            valign: "top",
          },
          headStyles: {
            fillColor: [214, 234, 248],
            fontStyle: "bold",
            textColor: [0, 0, 0],
            halign: "left",
          },
          bodyStyles: { textColor: [0, 0, 0] },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 50 },
            2: { cellWidth: 35 },
            3: { cellWidth: 40 },
          },
          margin: { left: leftMargin, right: rightMargin },
        });

        y = (doc as any).lastAutoTable.finalY + 12;
      }

      /* Transactions */
      if (data.content.Transactions && data.content.Transactions.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text("Transactions", leftMargin, y);
        y += 6;

        for (const transaction of data.content.Transactions) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text(
            `${transaction.Event} - ${formatDate(transaction.Date)}`,
            leftMargin + 4,
            y
          );
          y += 5;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          const detailsLines = doc.splitTextToSize(
            transaction.Details,
            contentWidth - 8
          );
          doc.text(detailsLines, leftMargin + 8, y);
          y += detailsLines.length * 4 + 3;

          if (
            transaction.Sell_Side_Advisors &&
            transaction.Sell_Side_Advisors.length > 0
          ) {
            const sellText = `Sell Side Advisors: ${transaction.Sell_Side_Advisors.join(
              ", "
            )}`;
            const sellLines = doc.splitTextToSize(sellText, contentWidth - 8);
            doc.text(sellLines, leftMargin + 8, y);
            y += sellLines.length * 4 + 2;
          }

          if (
            transaction.Buy_Side_Advisors &&
            transaction.Buy_Side_Advisors.length > 0
          ) {
            const buyText = `Buy Side Advisors: ${transaction.Buy_Side_Advisors.join(
              ", "
            )}`;
            const buyLines = doc.splitTextToSize(buyText, contentWidth - 8);
            doc.text(buyLines, leftMargin + 8, y);
            y += buyLines.length * 4 + 5;
          }

          // Check if we need a new page
          if (y > pageHeight - 20) {
            doc.addPage();
            y = leftMargin;
          }
        }
      }

      /* Watermark + Footer */
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      doc.save(`Ownership_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF: " + error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadWord = async () => {
    if (!data) return;
    setDownloadingWord(true);

    try {
      const {
        Document,
        Paragraph,
        Packer,
        HeadingLevel,
        AlignmentType,
        Table,
        TableRow,
        TableCell,
        WidthType,
        TextRun,
        Footer,
        ImageRun,
      } = await import("docx");

      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const footerChildren: Paragraph[] = [
        new Paragraph({
          children: [
            new TextRun({
              text: `Confidential - Generated by ${tenant?.name || "Company"}`,
              size: 10,
              color: "888888",
            }),
          ],
        }),
      ];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: { width: 60, height: 60 },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      const doc = new Document({
        sections: [
          {
            footers: {
              default: new Footer({ children: footerChildren }),
            },
            children: [
              new Paragraph({
                text: "Ownership - Company Overview",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                spacing: { after: 200, before: 100 },
              }),
              new Paragraph({
                text: `Generated on ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
                spacing: { after: 300 },
              }),
              new Paragraph({
                text: "Ownership Information",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                text: `Ownership Type: ${data.content.Ownership}`,
                spacing: { after: 50 },
              }),
              new Paragraph({
                text: `Primary Owner: ${data.content.Primary_Owner}`,
                spacing: { after: 50 },
              }),
              new Paragraph({
                text: `Ownership Percentage: ${data.content.Ownership_Percent}`,
                spacing: { after: 50 },
              }),
              new Paragraph({
                text: `Owner Type: ${data.content.Ownership_Type}`,
                spacing: { after: 200 },
              }),
              ...(data.content.Other_Shareholders &&
              data.content.Other_Shareholders.length > 0
                ? [
                    new Paragraph({
                      text: "Other Shareholders",
                      heading: HeadingLevel.HEADING_2,
                      spacing: { before: 200, after: 100 },
                    }),
                    new Table({
                      rows: [
                        new TableRow({
                          children: [
                            new TableCell({
                              children: [new Paragraph({ text: "Name" })],
                              shading: { fill: "D6EAFF" },
                            }),
                            new TableCell({
                              children: [new Paragraph({ text: "Company" })],
                              shading: { fill: "D6EAFF" },
                            }),
                            new TableCell({
                              children: [
                                new Paragraph({ text: "Ownership %" }),
                              ],
                              shading: { fill: "D6EAFF" },
                            }),
                            new TableCell({
                              children: [new Paragraph({ text: "Type" })],
                              shading: { fill: "D6EAFF" },
                            }),
                          ],
                        }),
                        ...data.content.Other_Shareholders.map(
                          (shareholder) =>
                            new TableRow({
                              children: [
                                new TableCell({
                                  children: [
                                    new Paragraph(shareholder.Name || ""),
                                  ],
                                }),
                                new TableCell({
                                  children: [
                                    new Paragraph(
                                      shareholder.Company_Name || ""
                                    ),
                                  ],
                                }),
                                new TableCell({
                                  children: [
                                    new Paragraph(
                                      shareholder.Ownership_Percent || ""
                                    ),
                                  ],
                                }),
                                new TableCell({
                                  children: [
                                    new Paragraph(shareholder.Type || ""),
                                  ],
                                }),
                              ],
                            })
                        ),
                      ],
                      width: { size: 100, type: WidthType.PERCENTAGE },
                      columnWidths: [2000, 3000, 2000, 2000],
                    }),
                    new Paragraph({ text: "", spacing: { after: 200 } }),
                  ]
                : []),
              ...(data.content.Transactions &&
              data.content.Transactions.length > 0
                ? [
                    new Paragraph({
                      text: "Transactions",
                      heading: HeadingLevel.HEADING_2,
                      spacing: { before: 200, after: 100 },
                    }),
                    ...data.content.Transactions.flatMap((transaction) => [
                      new Paragraph({
                        text: `${transaction.Event} - ${formatDate(
                          transaction.Date
                        )}`,

                        spacing: { before: 100, after: 50 },
                      }),
                      new Paragraph({
                        text: transaction.Details,
                        spacing: { after: 50 },
                      }),
                      ...(transaction.Sell_Side_Advisors &&
                      transaction.Sell_Side_Advisors.length > 0
                        ? [
                            new Paragraph({
                              text: `Sell Side Advisors: ${transaction.Sell_Side_Advisors.join(
                                ", "
                              )}`,
                              spacing: { after: 50 },
                            }),
                          ]
                        : []),
                      ...(transaction.Buy_Side_Advisors &&
                      transaction.Buy_Side_Advisors.length > 0
                        ? [
                            new Paragraph({
                              text: `Buy Side Advisors: ${transaction.Buy_Side_Advisors.join(
                                ", "
                              )}`,
                              spacing: { after: 100 },
                            }),
                          ]
                        : []),
                    ]),
                  ]
                : []),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `Ownership_${new Date().toISOString().slice(0, 10)}.docx`);
    } catch (error) {
      console.error("Word generation failed:", error);
      alert("Failed to download the Word document: " + error);
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl m-auto mt-20 p-6 bg-[#FAFBFD] rounded-xl shadow-xs border border-gray-200 animate-pulse">
        {/* Header skeleton */}
        <div className="h-8 w-1/3 bg-gray-300 rounded mb-4"></div>

        {/* Primary owner row */}
        <div className="grid grid-cols-12 gap-4 mb-3">
          <div className="col-span-4 h-5 bg-gray-300 rounded"></div>
          <div className="col-span-2 h-5 bg-gray-300 rounded"></div>
          <div className="col-span-3 h-5 bg-gray-300 rounded"></div>
          <div className="col-span-3"></div>
        </div>

        {/* Other shareholders skeleton rows */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="grid grid-cols-12 gap-4 items-center mb-3">
            <div className="col-span-4 h-5 bg-gray-300 rounded"></div>
            <div className="col-span-2 h-5 bg-gray-300 rounded"></div>
            <div className="col-span-3 h-5 bg-gray-300 rounded"></div>
            <div className="col-span-3 h-5 bg-gray-300 rounded"></div>
          </div>
        ))}

        {/* Timeline skeleton */}
        <div className="mt-8 space-y-12">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex items-start relative">
              <div className="w-44 text-right pr-4 flex-shrink-0 space-y-2">
                <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </div>
              <div className="w-3 h-3 rounded-full bg-gray-300 absolute left-[10.625rem] top-[2rem]"></div>
              <div className="flex-1 ml-6 mt-7 space-y-2">
                <div className="h-4 bg-gray-300 rounded w-full"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data?.content) {
    return (
      <p className="text-center py-6 text-red-500">
        No ownership data available
      </p>
    );
  }

  const content = data.content;

  return (
    <>
      <div className="bg-[#FDFDFD] rounded-xl max-w-4xl m-auto mt-0 2xl:mt-20  shadow-xs border border-gray-200 ">
        {/* Header Section with Background */}
        <div className="flex gap-2 justify-end mt-2">
          <button
            onClick={downloadPDF}
            disabled={downloadingPdf}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingPdf ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" /> PDF
              </>
            )}
          </button>

          <button
            onClick={downloadWord}
            disabled={downloadingWord}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingWord ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" />
                Word
              </>
            )}
          </button>
        </div>
        <div className="p-6 bg-[#FAFBFD] rounded-t-2xl flex-shrink-0 border-b border-[#E5E7EB]">
          <h2 className="text-2xl font-light text-gray-900 mb-4">
            Ownership: {content.Ownership}
          </h2>

          <div className="space-y-3">
            {/* Primary Owner */}
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* Owner */}
              <div className="col-span-4">
                <span className="text-base font-normal text-gray-900">
                  {content.Primary_Owner}
                </span>
              </div>

              {/* Percentage  */}
              <div className="col-span-4 text-center">
                <span className="text-base text-gray-900">
                  {content.Ownership_Percent}
                </span>
              </div>

              {/* Ownership Type */}
              <div className="col-span-4">
                <span className="text-base font-light text-gray-900">
                  {content.Ownership_Type}
                </span>
              </div>
            </div>

            {/* Other Shareholders */}
            {content.Other_Shareholders?.map((owner, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4">
                  <span
                    className={`text-base font-normal ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Name}
                  </span>
                </div>
                <div className="col-span-4 text-center">
                  <span
                    className={`text-base ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Ownership_Percent}
                  </span>
                </div>
                <div className="col-span-4">
                  <span
                    className={`text-base font-light ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="p-6 relative flex-1 overflow-y-auto">
          <div className="absolute left-50 top-0 bottom-0 w-0.5 border-l border-dashed border-gray-300"></div>

          <div className="space-y-12">
            {content.Transactions?.map((transaction, index) => {
              const { firstRow, secondRow } = formatEventName(
                transaction.Event
              );

              return (
                <div key={index} className="relative flex items-start">
                  <div className="w-44 text-right pr-4 flex-shrink-0">
                    <p className="text-sm text-gray-500 mb-2 font-sans">
                      {formatDate(transaction.Date)}
                    </p>

                    <div className="text-sm text-gray-700 font-medium">
                      <p>{firstRow}</p>
                      {secondRow && <p className="mt-1">{secondRow}</p>}
                    </div>
                  </div>

                  <div className="w-3 h-3 rounded-full bg-gray-200 shadow absolute left-[10.625rem] top-[2rem] z-10"></div>

                  <div className="flex-1 ml-6 mt-[1.75rem]">
                    <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light">
                      {transaction.Details}
                    </p>

                    <div className="space-y-1 mb-2">
                      <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light">
                        <span className="">Sell side advisors:</span>{" "}
                        {transaction.Sell_Side_Advisors?.join(", ")}
                      </p>
                      <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light">
                        <span className="">Buy side advisors:</span>{" "}
                        {transaction.Buy_Side_Advisors?.join(", ")}
                      </p>
                    </div>

                    {transaction.Sources && transaction.Sources.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {transaction.Sources.map((source, sourceIndex) => (
                          <div
                            key={sourceIndex}
                            className="relative inline-flex items-center group"
                          >
                            <button
                              onClick={(e) => handleSourceClick(e, source.URL)}
                              className="inline-block text-xs text-gray-700 border border-gray-300 font-medium px-1.5 py-0.5 rounded-xl shadow-sm hover:bg-gray-50 transition-colors"
                            >
                              {getHostname(source.URL)}
                            </button>
                            <button
                              onClick={() => handleOpenInNewTab(source.URL)}
                              className="ml-1 p-0.5 text-gray-400 hover:text-gray-600 transition-colors opacity-0 group-hover:opacity-100"
                              title="Open in new tab"
                            >
                              <ImNewTab className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Iframe Viewer for Sidebar */}
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </>
  );
};
